import { fetchInvoiceList, invoiceInfo, SearchInvoiceInfo } from "../../redux/slice/invoice/InvoiceSlice";
import ExcelJS from 'exceljs';
import type { AppDispatch } from "../../redux/store";
import { fetchOrdersList, OrderInfo, OrderItemDTO } from "../../redux/slice/order/OrderSlice";
import { COMMISSION_TYPE_CODETABLE, EXPORTABLE_ITEMS, ORDER_AVAILABILITY, PRODUCT_CATEGORY, USER_ROLES } from "../../constants/applicationConstants";
import { EXCEL_FINANCE_HQ_V3_EXPORT_COLUMNS } from "../../constants/excelFieldKeys";
import { fetchIVRList } from "../../redux/slice/ivr/createIVRSlice";
import { searchAgency } from "../../redux/slice/agency/AgencySlice";
import { formatAddress, formatDateTime, formatPaymentMethod, fullName, getCategoryDisplayLabel, getCodeTableDisplayLabel, isV3CommissionType, priceFormatter } from "../../utils/Utility";
import { searchPracticeList } from "../../redux/slice/practiceSlice";
import { fetchAuditLogList } from "../../redux/slice/auditLog/auditLogSlice";
import { PaymentElementInfo, PaymentInfo, searchPayments } from "../../redux/slice/payment/paymentSlice";
import { fetchProductAgreementList, fetchSKUProductAgreementList, SKUProductAgreementRequest, useCreateAgreementSlice } from "../../redux/slice/productAgreement/createAgreementSlice";
import { fetchManufacturerList } from "../../redux/slice/manufacturer/ManufacturerSlice";
import { searchProviders } from "../../redux/slice/practice/practiceProvidersSlice";
import { searchUserList } from "../../redux/slice/userSlice";
import { fetchProductsList } from "../../redux/slice/product/productListSlice";
import { searchBaaDetails } from "../../redux/slice/baa/createBusinessAgreementSlice";
import { searchPracticesKpiList, searchUserKpiList, searchWeeklySalesKpiList } from "../../redux/slice/dashboard/dashboardSlice";
import { PatientInfo, searchPatients } from "../../redux/slice/patient/patientSlice";
import { fetchServiceSubscriptionList } from "../../redux/slice/serviceSubscriptionSlice";

interface invoiceTableRow extends invoiceInfo {
  checkBox: JSX.Element;
  payments: PaymentElementInfo[];
}

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
  padding?: string;
  props?: ColumnProps;
}

interface ColumnProps {
  control?: any;
  controllerName?: string;
  isDisabled?: boolean;
}

type IVRExportInfo = {
  ivrId: number;
  productName: string; //CS-1625
  manufacturerName: string; //CS-1625
  ivrNumber: string;
  ivrInfo: {
    woundInfo: {
      woundData: {
        woundType: string;
        totalSize: string;
        otherDxCodes: string;
      }[]
    }
    providerInfo: {
      provider: {
        npi: string;
      }
    }
    treatmentInfo: {
      placesOfService: string[]
      currentlyUnderSurgicalGlobalPeriod: string;
      currentlyInNursingFacility: String;
      daysAdmittedToFacility: string;
      surgicalProcedureCodes: string;
      surgicalProcedureDate: string;
    }
  }
  patientDOB: string;
  patientFirstName: string;
  patientId: number;
  patientCode: string;   // CS-1344
  patientLastName: string;
  patientMiddleName: string;
  patientinfoData: {
    insuranceName: string;
    insuranceType: string;
    policy: string
    type: string;
  };
  infoType: string;
  // CS-1344 - Start
  practiceName: string;
  createdDate: string;
  dateOfService: string;
  verificationStatus: string;
  // CS-1344 - End
  practiceNpi: string;
  practiceProviderNpi: string;
  secondaryInsurance: {
    insuranceName: string;
    insuranceType: string;
    policy: string
    type: string;
  }
  primaryInsurance: {
    insuranceName: string;
    insuranceType: string;
    policy: string
    type: string;
  }
  dateOfSurgery: string;  
}
// CS-1156 - Start
type AgencyExportInfo = {
  agencyId: number;
  agencyName: string;
  phone: string;
  email: string;
  streetAddress: string;
  unitNumber: string;
  city: string;
  state: string;
  zipCode: string;
  commissionType: string;
  inactiveDate: string;
  practiceName?: string;
  productInfo?: string;
  accountRep?: string;
};
// CS-1156 - End

// CS-1370 - Start
type PracticeExportInfo = {
  practiceId: string;
  practiceName: string;
  contactFirstName: string;
  contactMiddleName: string;
  contactLastName: string;
  title: string;
  contactEmail: string;
  contactPhone: string;
  approvalStatus: string;
  requestTimestamp: string;
  agencyId: string;
  agencyName: string;
  practiceProviderNames?: string[];
  executivesNames: string[]; // Account Rep names
  practiceLocations?: {   // CS-1691 - Start
    streetAddress?: string;
    unitNumber?: string;
    city?: string;
    state?: string;
    zipCode?: string;  // CS-1691 - End
  }[];
};
// CS-1370 - End

// CS-1405 - Start
type AuditLogExportInfo = {
  entityName: string;
  entityNumber: string;
  actionType: string | string[];
  userName: string;
  action: string;
  clientIP: string;
  auditTimestamp: string;
};
// CS-1405 - End

// CS-1608 - Start
type ProductAgreementExportInfo = {
  practiceProductId: number,
  productId: number,
  productName: string;
  practiceName: string;
  contactEmail: string;
  contactPhone: string;
  effectiveDate: string;
  agreementStatus: string;
  apFirstName: string;
  apMiddleName: string;
  apLastName: string;
  apPhone: string;
  apEmail: string;
  manufacturerName: string;
  extendedPrice: number;
  hasSKUPrice: boolean;
  discountType: string;
  skuProducts: {
    discountType: string,
    extendedPrice: number,
    practiceProductId: number,
    practiceProductPriceId: number,
    productItemDescription: string,
    productItemId: number,
    productItemSku: string,
    productPriceId: number,
  }[]
};
// CS-1685 - Start
type ManufacturerExportInfo = {
  manufacturerId: number;
  manufacturerName: string;
  contactPhone: string;
  notificationEmail: string;
  customConfig: string;
  inactiveDate: string;
  cartOption: string;
  legalName: string;
}

type ProviderExportInfo = {
  providerId: number;
  firstName: string;
  middleName: string;
  lastName: string;
  title: string;
  npi: string;
  email: string;
  ptan: string;
  taxId: string;
  createdDate: string;
  approvedStatus: string;
  practiceName: string;
  hasUserAccount: boolean;
}

type ProductExportInfo = {
  productId: number;
  productName: string;
  productDescription: string;
  manufacturer: {
    manufacturerName: string;
  }
  productCategory: string;
  hcpcsCode: string;
  productStatus: string;
  insuranceCoverage?: string;
  productItems?: {
    productItemSku: string;
    size: string;
    units: string;
    inactiveDate?: string;
    productItemPrices?: Array<{
      listPrice: number;
      customPrice?: number;
    }>
  }[];
  productPricing?: string;
  productPrices?: {
    listPrice: number;
    customPrice?: number;
  }
}

type BaaExportInfo = {
  baaDetailId: number;
  agreementDocumentId: number;
  agreementStatus: string;
  agreementType: string;
  auditTimestamp: string;
  email: string;
  inactiveDate: string;
  userType: string;
  practiceName: string;
  userSignedDate: string;
}
//CS-1700 - Start
type PatientExportInfo = {
  patientId: number;
  patientName: string;
  practiceName: string;
  dob: string;
  dateOfSurgery: string;
  status: string;
  patientCode: string;
};
type PatientDetailExportInfo = {
  patientId: number;
  patientName: string;
  practiceName: string;
  productName: string;
  contactEmail: string;
  contactPhone: string;
  effectiveDate: string;
  agreementStatus: string;
  manufacturerName: string;
  apFirstName: string;
  apMiddleName?: string;
  apLastName: string;
  apPhone: string;
  apEmail: string;
  discount: string;
  extendedPrice: number;
  hasSKUPrice?: boolean;
  skuProducts?: {
    discountType: string,
    extendedPrice: number,
    practiceProductId: number,
    practiceProductPriceId: number,
    productItemDescription: string,
    productItemId: number,
    productItemSku: string,
    productPriceId: number,
  }[]
};
//CS-1700 - End
// CS-1706 - Start
type ServiceSubscriptionExportInfo = {
  serviceSubscriptionId: number;
  serviceName: string;
  userName: string;
  userEmail: string;
  signDatetime: string;
  status: string;
  signedDocumentId?: number;
};
// CS-1706 - End
// CS-1685 - End
// CS-1608 - End

type UserExportInfo = {
  userId: string;
  email: string;
  userRole: string;
  userName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  lastLoginAt: string;
  userStatus: string;
  createDate: string;
  practiceName?: string;
};

type UserWithRoleDTO = {
  userId: number;
  email: string;
  firstName: string;
  middleName: string;
  lastName: string;
  roleName: string;
  roleDisplayValue: string;
  lastLoginAt: string;
};

type ActivePracticesDTO = {
  practiceId: number;
  orderCount: number;
  practiceName: string;
  ivrCount: number;
}

type PracticeSalesSummaryDTO = {
  practiceId: number;
  practiceName: string;
  totalSalesAmount: number;
  numberOfOrders: number;
  percentageOfTotalSales: number;
};

// Prevent Excel export corruption
const sanitizeExcelText = (value: any) => {
  if (typeof value !== 'string') return value;

  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .substring(0, 32767);
};

const ExportToExcel = async (initData: any, formattedColumns: any, dispatch: AppDispatch, sectionName: string, hiddenColumns?: string[]) => {
  const formatSizes = (orderItems: OrderItemDTO[]) => {
    const uniqueSizes = new Set();
    orderItems.forEach((orderItem) => {
      if (orderItem.size) {

        const size = orderItem.size.trim();
        const match = size.match(/(\d+(\.\d+)?x\d+(\.\d+)?)\s*cm|(\d+mm\b.*)/gi);
        if (match) {
          uniqueSizes.add(match[0].replace(/\s*cm$/, ""));
        }
      }
    });


    return Array.from(uniqueSizes).join("\n");
  };
  const { store } = await import("../../redux/store");
  const allCodeTablesValuesMap = store.getState().codetableSlice.allCodeTablesValuesMap;

  const getInitials = (patientName: string, hasMiddleName: boolean) => {
    if (!patientName) return "";
    const names = patientName.trim().split(' ');

    if (!hasMiddleName) {
      return names.map(name => name.charAt(0).toUpperCase()).join('');
    } else {
      return names
        .filter((_, i, arr) => i === 0 || i < arr.length - 1)
        .map(name => name.charAt(0).toUpperCase())
        .join('');
    }
  };

  try {
    // CS-1156 - Start
    let allRecords: invoiceTableRow[] | IVRExportInfo[] | OrderInfo[] | AgencyExportInfo[] | PracticeExportInfo[] | AuditLogExportInfo[] | ProductAgreementExportInfo[] | PaymentInfo[] | UserExportInfo[] | ManufacturerExportInfo[] | ProviderExportInfo[] | ProductExportInfo[] | BaaExportInfo[] | UserWithRoleDTO[] | ActivePracticesDTO[] | PatientExportInfo[] | PatientDetailExportInfo[] | PatientInfo[] | PracticeSalesSummaryDTO[] | ServiceSubscriptionExportInfo[] = []; // CS-1370 // CS-1405  // CS-1685 // CS-1706
    let response: any = null;
    let fetchedData: invoiceTableRow[] | OrderInfo[] | IVRExportInfo[] | AgencyExportInfo[] | PracticeExportInfo[] | AuditLogExportInfo[] | ProductAgreementExportInfo[] | PaymentInfo[] | UserExportInfo[] | ManufacturerExportInfo[] | ProviderExportInfo[] | ProductExportInfo[] | BaaExportInfo[] | UserWithRoleDTO[] | ActivePracticesDTO[] | PatientExportInfo[] | PatientDetailExportInfo[] | PatientInfo[] | PracticeSalesSummaryDTO[] | ServiceSubscriptionExportInfo[] = []; // CS-1370 // CS-1405 // CS-1685 // CS-1706
    // CS-1156 - End
    if (sectionName === EXPORTABLE_ITEMS.INVOICE || sectionName === EXPORTABLE_ITEMS.COMMISSION) {
      response = await dispatch(fetchInvoiceList(initData));
      fetchedData = response.payload?.data?.downloadData || [];
      allRecords = [...(allRecords as invoiceTableRow[]), ...(fetchedData as invoiceTableRow[])];

    } else if (sectionName === EXPORTABLE_ITEMS.IVR|| sectionName === EXPORTABLE_ITEMS.IVR_LIST) {
      response = await dispatch(fetchIVRList({data:initData, skipLoader:false}));
      fetchedData = response.payload?.data?.downloadIvrData.content || [];
      allRecords = [...(allRecords as IVRExportInfo[]), ...(fetchedData as IVRExportInfo[])];
      // CS-1156 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.AGENCY_LIST || sectionName === EXPORTABLE_ITEMS.AGENCY_DETAILS) {
      response = await dispatch(searchAgency(initData));
      fetchedData = response.payload?.data?.downloadAgencyData.content || [];
      allRecords = [...(allRecords as AgencyExportInfo[]), ...(fetchedData as AgencyExportInfo[])];
      // CS-1156 - End
      // CS-1370 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PRACTICE || sectionName === EXPORTABLE_ITEMS.PRACTICE_DETAILS) {
      response = await dispatch(searchPracticeList(initData));
      fetchedData = response.payload?.data?.downloadPracticeData.content || [];
      allRecords = [...(allRecords as PracticeExportInfo[]), ...(fetchedData as PracticeExportInfo[])];
      // CS-1370 - End
    } else if (sectionName === EXPORTABLE_ITEMS.ORDER || sectionName === EXPORTABLE_ITEMS.ORDER_SSV) {
      response = await dispatch(fetchOrdersList({data: initData, skipLoader:false}));
      fetchedData = response.payload?.data?.downloadData || [];
      allRecords = [...(allRecords as OrderInfo[]), ...(fetchedData as OrderInfo[])];

      // CS-1405 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.AUDIT_LOG) {
      response = await dispatch(fetchAuditLogList(initData));
      fetchedData = response.payload?.data?.downloadAuditData?.content || [];
      allRecords = [...(allRecords as AuditLogExportInfo[]), ...(fetchedData as AuditLogExportInfo[])];
      // CS-1608 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.AGREEMENT || sectionName === EXPORTABLE_ITEMS.AGREEMENT_LIST) {
      response = await dispatch(fetchProductAgreementList(initData));
      fetchedData = response.payload?.downloadProductAgreementData?.content || [];
      allRecords = [...(allRecords as ProductAgreementExportInfo[]), ...(fetchedData as ProductAgreementExportInfo[])];
      // CS-1608 - End
    } else if (sectionName === EXPORTABLE_ITEMS.PAYMENT) {
      response = await dispatch(searchPayments(initData));
      fetchedData = response.payload?.data?.downloadPaymentList?.content || [];
      allRecords = [...(allRecords as PaymentInfo[]), ...(fetchedData as PaymentInfo[])];
      // CS-1685 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.MANUFACTURER || sectionName === EXPORTABLE_ITEMS.MANUFACTURER_DETAILS) {
      response = await dispatch(fetchManufacturerList(initData));
      fetchedData = response.payload?.data?.downloadManufacturerData?.content || [];
      allRecords = [...(allRecords as ManufacturerExportInfo[]), ...(fetchedData as ManufacturerExportInfo[])];
      //CS-1700 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.PATIENTS) {
      response = await dispatch(searchPatients(initData));
      fetchedData = response.payload?.downloadPatientData?.content || [];
      allRecords = [...(allRecords as PatientExportInfo[]), ...(fetchedData as PatientExportInfo[])];

    } else if (sectionName === EXPORTABLE_ITEMS.PATIENT_DETAILS) {
      response = await dispatch(searchPatients(initData));
      console.log("Patient Export Response:", response.payload);
      // const patients = response.payload?.searchPatientData?.content || [];
      fetchedData = response.payload?.downloadPatientData?.content || [];
      console.log("Patient Export fetchedData:", fetchedData);
      allRecords = [...(allRecords as PatientDetailExportInfo[]), ...(fetchedData as PatientDetailExportInfo[])];
      console.log(allRecords);

    } else if (sectionName === EXPORTABLE_ITEMS.PROVIDER) {
      response = await dispatch(searchProviders(initData));
      fetchedData = response.payload?.downloadPracticeProviderData?.content || [];
      allRecords = [...(allRecords as ProviderExportInfo[]), ...(fetchedData as ProviderExportInfo[])];
      // CS-1685 - End
    } else if (sectionName === "USER" || sectionName === EXPORTABLE_ITEMS.USER_DETAILS) {
      response = await dispatch(searchUserList(initData));
      console.log("USER Export Response:", response.payload);
      // Try multiple possible response structures to match other APIs
      fetchedData = response.payload?.data?.downloadUserData?.content || 
                   response.payload?.data?.downloadUserData || 
                   response.payload?.downloadUserData?.content || 
                   response.payload?.downloadUserData || 
                   [];
      console.log("USER Export fetchedData:", fetchedData);
      allRecords = [...(allRecords as UserExportInfo[]), ...(fetchedData as UserExportInfo[])];
      // CS-1685 - Start
    } else if (sectionName === EXPORTABLE_ITEMS.PRODUCTS || sectionName === EXPORTABLE_ITEMS.PRODUCTS_DETAILS) {
      response = await dispatch(fetchProductsList({searchParams: initData}));
      fetchedData = response?.payload?.data?.downloadProductData?.content || [];
      allRecords = [...(allRecords as ProductExportInfo[]), ...(fetchedData as ProductExportInfo[])];

    } else if (sectionName === EXPORTABLE_ITEMS.BAA) {
      response = await dispatch(searchBaaDetails(initData));
      fetchedData = response.payload?.downloadBaaDetailData?.content || [];
      allRecords = [...(allRecords as BaaExportInfo[]), ...(fetchedData as BaaExportInfo[])];
      // CS-1685 - End
      // CS-1679 Start
    } else if (sectionName === "activeUsers") {
      response = await dispatch(searchUserKpiList(initData));
      fetchedData = response.payload?.downloadActiveUsersData?.content || [];
      allRecords = [...(allRecords as UserWithRoleDTO[]), ...(fetchedData as UserWithRoleDTO[])];
    } else if (sectionName === "activePractices") {
      response = await dispatch(searchPracticesKpiList(initData));
      fetchedData = response.payload?.data?.downloadActivePracticesData?.content || [];
      allRecords = [...(allRecords as ActivePracticesDTO[]), ...(fetchedData as ActivePracticesDTO[])];
    } else if (sectionName === "totalPatients") {
      response = await dispatch(searchPatients(initData));
      fetchedData = response.payload?.downloadPatientData?.content || [];
      allRecords = [...(allRecords as PatientInfo[]), ...(fetchedData as PatientInfo[])];
    } else if (sectionName === "weeklySales") {
      response = await dispatch(searchWeeklySalesKpiList(initData));
      fetchedData = response.payload?.data?.downloadPracticesWeeklySalesData?.content || [];
      allRecords = [...(allRecords as PracticeSalesSummaryDTO[]), ...(fetchedData as PracticeSalesSummaryDTO[])];
    } else if (sectionName === "weeklyOrders") {
      response = await dispatch(searchWeeklySalesKpiList(initData));
      fetchedData = response.payload?.data?.downloadPracticesWeeklySalesData?.content || [];
      allRecords = [...(allRecords as PracticeSalesSummaryDTO[]), ...(fetchedData as PracticeSalesSummaryDTO[])];
    }
    // CS-1679 End
    // CS-1706 START - Service Subscription Export
    else if (sectionName === EXPORTABLE_ITEMS.SERVICE_SUBSCRIPTION) {
      response = await dispatch(fetchServiceSubscriptionList(initData));
      fetchedData = response.payload?.data?.content || [];
      allRecords = [...(allRecords as ServiceSubscriptionExportInfo[]), ...(fetchedData as ServiceSubscriptionExportInfo[])];
    }
    // CS-1706 END

    // CS-1728 START - Explore Market Export (in-memory filtered data, no API call)
    else if (sectionName === EXPORTABLE_ITEMS.EXPLORE_MARKET) {
      // CS-1765 - Explore Market export includes top-level purchaseAndBill from filtered records
      allRecords = [...(initData.records ?? [])];
    }
    // CS-1728 END

    const headerRow = [];

    if (allRecords.length > 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheetName = sectionName === EXPORTABLE_ITEMS.COMMISSION
        ? "Service Fee Search List"
        : sectionName;
      const worksheet = workbook.addWorksheet(worksheetName);

      if (sectionName === EXPORTABLE_ITEMS.ORDER) {

        const headerRow = [
          'Order ID', 'Order Date', 'Practice', 'PT', 'Product',
          'QTY', 'Size', 'Units', 'COGs/Unit', 'COGs', 'Provider',
          'Date Of Service', 'Actual DOS', 'Order Type', 'Order Status',//CS-1385 - Label Change
          'Usage Status', 'Serial Number'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 20;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        const cogsUnitIndex = headerRow.indexOf('COGs/Unit') + 1;
        const cogsIndex = headerRow.indexOf('COGs') + 1;

        allRecords.forEach(order => {
          const orderInfo = order as OrderInfo;

          if ("orderItems" in order && Array.isArray(order.orderItems)) {
            order.orderItems.forEach((orderItem: OrderItemDTO) => {
              const rowData = headerRow.map(header => {
                switch (header) {
                  case 'Order ID': //CS-1377
                    return orderInfo.orderNumber;
                  case 'Order Date':
                    return orderInfo.orderDate;
                  case 'Practice':
                    return orderInfo.practiceName;
                  case 'PT':
                    return getInitials(orderInfo.patientName, orderInfo.hasMiddleName);
                  case 'Product':
                    return orderInfo.productCategory === PRODUCT_CATEGORY.CONSERVATIVE
                      ? getCategoryDisplayLabel(allCodeTablesValuesMap, orderInfo.productCategory)
                      : orderInfo.productName;
                  case 'QTY':
                    return orderItem.quantity;
                  case 'Size':
                    return orderItem.size;
                  case 'Units':
                    return orderItem.units;
                  case 'COGs/Unit':
                    return (orderItem.extendedPrice ? orderItem.extendedPrice : "N/A");
                  case 'COGs':
                    //CS-1560 - Removing calulation logic from UI side
                    return orderItem.orderItemPrice ? orderItem.orderItemPrice : "N/A";
                  // return orderItem.hasSKUPrice ? orderItem.extendedPrice : (orderItem.extendedPrice ? orderItem.units * orderItem.extendedPrice : "N/A");
                  case 'Provider':
                    return orderInfo.providerName || orderInfo.orderInfo?.provider || '';
                  case 'Date Of Service':
                    return orderInfo.dateOfService;
                  case 'Actual DOS':
                    return (orderItem.actualDateOfService ? orderItem.actualDateOfService : 'N/A');
                  case 'Order Type':
                    return orderInfo.orderType;
                  case 'Order Status': //CS-1385 - Label Change 
                    const trackingNumber = orderInfo.trackingNumber;
                    return trackingNumber
                      ? `${orderInfo.orderStatus} (${trackingNumber})`
                      : orderInfo.orderStatus;
                  case 'Usage Status':
                    if (orderItem?.tissueTrackingDocumentId) {
                      if (orderItem?.invoiceId) {
                        return "Applied (Invoiced)";
                      }
                      return "Applied";
                    } else if (orderItem?.productReturnId) {
                      if (orderItem?.productReturnStatus === ORDER_AVAILABILITY.RETURN_INITIATED) {
                        if (orderItem?.invoiceId) {
                          return "Return Initiated (Invoiced)";
                        }
                        return "Return Initiated";
                      }
                      else if (orderItem?.productReturnStatus === ORDER_AVAILABILITY.RETURN_PENDING) {
                        if (orderItem?.invoiceId) {
                          return "Return Pending (Invoiced)";
                        }
                        return "Return Pending";
                      }
                      else if (orderItem?.productReturnStatus === ORDER_AVAILABILITY.RETURNED) {
                        if (orderItem?.invoiceId) {
                          return "Returned (Invoiced)";
                        }
                        return "Returned";
                      }
                      else if (orderItem?.productReturnStatus === ORDER_AVAILABILITY.RETURN_REJECTED) {
                        if (orderItem?.invoiceId) {
                          return "Return Rejected (Invoiced)";
                        }
                        return "Available(Return Rejected)";
                      }

                      if (orderItem?.invoiceId) {
                        return "Returned (Invoiced)";
                      }
                      return "Returned";
                    } else if (orderItem?.invoiceId) {
                      return "Invoiced";
                    }
                    return "Available";
                  case 'Serial Number':
                    return orderItem.serialNumber;
                  default:
                    return '';
                }
              });

              const newRow = worksheet.addRow(rowData);

              if (orderItem.extendedPrice) {
                newRow.getCell(cogsUnitIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
                newRow.getCell(cogsIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
              }
            });
          }
        });

      } else if (sectionName === EXPORTABLE_ITEMS.ORDER_SSV) {
        //CS-1385 - Label Change
        const headerRow = [
          'MFG', 'MFG Confirm #', 'Invoice ID', 'Tracking #', 'Order ID',
          'Serial Number', 'Order Date', 'Order Type', 'Order MO/YR', 'Practice',
          'Product', 'Size', 'Total Units/sqcm', 'Prod Qty', 'COGs P/U',
          'COGs/Sales Orig', 'COGs/Sales Pmt', 'Provider', 'Date Of Service', 'PT',
          'Order Status', 'Usage Status' // CS-1692
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 20;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        const cogsUnitIndex = headerRow.indexOf('COGs P/U') + 1;
        const cogsSalesOrig = headerRow.indexOf('COGs/Sales Orig') + 1;
        const cogsSalesPmt = headerRow.indexOf('COGs/Sales Pmt') + 1;

        const finalRows: any[] = [];

        allRecords.forEach((order) => {
          const orderInfo = order as OrderInfo;
          const hasSkuPrice = orderInfo.orderItems?.some((item: any) => item.hasSKUPrice);
          const newRowData = headerRow.map((header) => {
            switch (header) {
              case "MFG":
                return orderInfo.manufacturerName ? orderInfo.manufacturerName : '';
              case "MFG Confirm #":
                return orderInfo.mfgConfirmationNumber ? orderInfo.mfgConfirmationNumber : '';
              case "Invoice ID": //CS-1385 - Label Change
                {
                  const uniqueInvoices = new Set();

                  "orderItems" in order && order.orderItems.forEach(orderItem => {
                    if (orderItem.mfgInvoiceNumber) {
                      uniqueInvoices.add(orderItem.mfgInvoiceNumber.trim());
                    }
                  });

                  const formattedInvoices = Array.from(uniqueInvoices).join("\n");
                  return formattedInvoices;
                }
              case "Tracking #":
                return orderInfo.trackingNumber ? orderInfo.trackingNumber : '';
              case 'Order ID': //CS-1377
                return orderInfo.orderNumber ? orderInfo.orderNumber : '';
              case 'Order Date':
                return orderInfo.orderDate ? orderInfo.orderDate : '';
              case 'Order MO/YR': {
                const orderDateString = orderInfo.orderDate;
                const orderDate = new Date(orderDateString);

                if (!isNaN(orderDate.getTime())) {
                  const monthShort = orderDate.toLocaleString('en-US', { month: 'short' });
                  const yearShort = orderDate.getFullYear().toString().slice(-2);
                  return `${monthShort} ${yearShort}'`;
                }

                return '';
              }
              case 'Practice'://CS-1385 - Label Change
                return orderInfo.practiceName ? orderInfo.practiceName : '';
              case 'PT':
                return getInitials(orderInfo.patientName, orderInfo.hasMiddleName);
              case 'Product':
                return orderInfo.productCategory === PRODUCT_CATEGORY.CONSERVATIVE
                  ? getCategoryDisplayLabel(allCodeTablesValuesMap, orderInfo.productCategory)
                  : (orderInfo.productName ? orderInfo.productName : '');
              case 'Prod Qty': {
                let totalQty = 0

                "orderItems" in order && order.orderItems.forEach(orderItem => {
                  if (orderItem.quantity) {
                    totalQty = totalQty + orderItem.quantity;
                  }
                });
                return totalQty;
              }
              case 'Size': {// modify this case'Size'for Prevent Excel export corruption
                const items = "orderItems" in order ? order.orderItems : [];

                let sizeValue = '';

                if (hasSkuPrice) {
                  const uniqueSizes = Array.from(
                    new Set(
                      items
                        .map(i => i.size?.trim())
                        .filter(Boolean)
                    )
                  );

                  sizeValue = uniqueSizes.join('\n');
                } else {
                  sizeValue = formatSizes(items);
                }

                return sanitizeExcelText(sizeValue);
              }
              case 'Total Units/sqcm': {
                if (hasSkuPrice) {
                  return "";
                } else {
                  let totalUnits = 0;
                  //CS-1560 - Updated logic
                  "orderItems" in order && order.orderItems.filter(i => i?.productReturnStatus !== "RETURNED")
                    .forEach(orderItem => {
                      totalUnits = totalUnits + orderItem.units;
                    });
                  return totalUnits;
                }
              }
              case 'COGs P/U':
                if (hasSkuPrice) {
                  return "";
                } else {
                  //CS-1560 - Updated logic
                   let totalUnits = 0;
                   
                  "orderItems" in order && orderInfo.orderItems
                    // exclude items that are RETURNED
                    .filter(i => i?.productReturnStatus !== "RETURNED")
                    .forEach(orderItem => {
                      totalUnits = totalUnits + orderItem.units;
                    });
                  // return (orderInfo.orderItems && orderInfo.orderItems.length > 0 && orderInfo.orderItems[0].extendedPrice)
                  //   ? orderInfo.orderItems[0].extendedPrice
                  //   : 0;

                  // add this Prevent Excel export corruption expect this [return (orderInfo.orderTotalPrice / totalUnits);]
                  return totalUnits > 0
                    ? (orderInfo.orderTotalPrice / totalUnits)
                    : 0;
                }
              case 'COGs/Sales Pmt':
                return "";
              case 'Provider':
                return orderInfo.providerName || orderInfo.orderInfo?.provider || '';
              case 'COGs/Sales Orig':
                //CS-1560 - Previous logic commented
                // if (hasSkuPrice) {
                //   const total = orderInfo.orderItems
                //     // exclude items that are RETURNED
                //     .filter(i => i?.productReturnStatus !== "RETURNED")
                //     .reduce((acc, i) => {
                //       if (typeof i?.extendedPrice === "number") {
                //         return acc + i.extendedPrice;
                //       }
                //       return acc;
                //     }, 0);
                //   return total;
                // } else {
                //   let totalCogSales = 0
                //   "orderItems" in order && order.orderItems.forEach(orderItem => {
                //     if (orderItem.extendedPrice && orderItem.units) {


                //       totalCogSales = totalCogSales + (orderItem.units * orderItem.extendedPrice);
                //     }
                //   });
                //   return totalCogSales;
                // }
                //CS-1560 - New logic
                const total = orderInfo.orderItems
                  // exclude items that are RETURNED
                  .filter(i => i?.productReturnStatus !== "RETURNED")
                  .reduce((acc, i) => {
                    if (typeof i?.orderItemPrice === "number") {
                      return acc + i.orderItemPrice;
                    }
                    return acc;
                  }, 0);
                return total;
              case 'Order Type':
                return orderInfo.orderType ? orderInfo.orderType : "";
              case 'Date Of Service':
                return orderInfo.dateOfService ? orderInfo.dateOfService : "";
              case 'Serial Number':
                {
                  const serialNumbers: string[] = [];
                  "orderItems" in order && order.orderItems.forEach(orderItem => {
                    if (orderItem.serialNumber) {
                      serialNumbers.push(orderItem.serialNumber);
                    }
                  });

                  const formattedInvoices = serialNumbers.join("\n");
                  return formattedInvoices;
                }
              //CS-1692 - Start
              case 'Order Status':
                const trackingNumber = orderInfo.trackingNumber;
                return trackingNumber
                  ? `${orderInfo.orderStatus} (${trackingNumber})`
                  : orderInfo.orderStatus;
              case 'Usage Status':
                const items = "orderItems" in order ? order.orderItems : [];

                if (items.some(i => i?.tissueTrackingDocumentId && i?.invoiceId)) {
                  return "Applied (Invoiced)";
                }

                if (items.some(i => i?.tissueTrackingDocumentId)) {
                  return "Applied";
                }

                const returnItem = items.find(i => i?.productReturnId);
                if (returnItem) {
                  const status = returnItem.productReturnStatus;
                  if (status === ORDER_AVAILABILITY.RETURN_INITIATED) {
                    return returnItem.invoiceId ? "Return Initiated (Invoiced)" : "Return Initiated";
                  }
                  else if (status === ORDER_AVAILABILITY.RETURN_PENDING) {
                    return returnItem.invoiceId ? "Return Pending (Invoiced)" : "Return Pending";
                  }
                  else if (status === ORDER_AVAILABILITY.RETURNED) {
                    return returnItem.invoiceId ? "Returned (Invoiced)" : "Returned";
                  }
                  else if (status === ORDER_AVAILABILITY.RETURN_REJECTED) {
                    return returnItem.invoiceId ? "Return Rejected (Invoiced)" : "Available(Return Rejected)";
                  }

                  return returnItem.invoiceId ? "Returned (Invoiced)" : "Returned";
                }

                if (items.some(i => i?.invoiceId)) {
                  return "Invoiced";
                }

                return "Available";
              //CS-1692 - End
              default:
                return '';
            }
          });
          //CS-1620 - Standardize  Formatting in Order SSV Export
          const row = worksheet.addRow(newRowData);
          // add this Prevent Excel export corruption expect this [row.getCell(cogsUnitIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';]
          const cogsUnitCell = row.getCell(cogsUnitIndex);
          if (typeof cogsUnitCell.value === 'number') {
            cogsUnitCell.numFmt =
              '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          }
          row.getCell(cogsSalesOrig).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          row.getCell(cogsSalesPmt).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          // row.getCell(cogsUnitIndex).numFmt = '$#,##0.00';
          // row.getCell(cogsSalesOrig).numFmt = '$#,##0.00';
          // row.getCell(cogsSalesPmt).numFmt = '$#,##0.00';

        });

        finalRows.forEach(rowData => {
          worksheet.addRow(rowData);
        });

      }
      else if (sectionName === EXPORTABLE_ITEMS.IVR_LIST) {
        const headerRow = [
          'IVR ID', 'Practice', 'Patient', 'Product', 'Manufacturer', 'Provider', 'Date Of Service', 'Status', 'Created'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 20;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(ivr => {
          const ivrInfo = { ...ivr } as IVRExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'IVR ID':
                return ivrInfo.ivrNumber;
              case 'Practice':
                return ivrInfo.practiceName || 'N/A';
              case 'Patient':
                return fullName(null, ivrInfo.patientFirstName, ivrInfo.patientMiddleName, ivrInfo.patientLastName);
              case 'Product':
                return ivrInfo.productName || 'N/A';
              case 'Manufacturer':
                return ivrInfo.manufacturerName || 'N/A';
              case 'Provider':
                return (ivrInfo as any).providerName || 
                  (ivrInfo.ivrInfo?.providerInfo?.provider?.npi ? ivrInfo.ivrInfo.providerInfo.provider.npi : 'N/A');
              case 'Date Of Service':
                return ivrInfo.dateOfService || 'N/A';
              case 'Status':
                return ivrInfo.verificationStatus || 'N/A';
              case 'Created':
                return ivrInfo.createdDate || 'N/A';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
      }
      else if (sectionName === EXPORTABLE_ITEMS.IVR) {
        // CS-1344 - Start
        //CS-1385 - Label Change
        const headerRow = [
          'IVR ID','Product','Manufacturer Name','Practice', 'Patient ID', 'Patient Name', 'Date of Service', 'IVR Status',
          'IVR Submission Date', 'Patient Date of Birth', 'Practice NPI', 'Provider NPI',
          'Primary Payer Company', 'Primary Payer Plan Type', 'Primary Payer Plan Policy ID', 'Primary Type',
          'Secondary Payer Company', 'Secondary Payer Plan Type', 'Secondary Payer Plan Policy ID', 'Secondary Type',
          'Surgical Global Period', 'Is in Skilled Nursing Home Facility', 'Passed 100 Days Mark in Skilled Nursing Home Facility',
          'DX Codes', 'Place of Service', 'Surgery Date', 'Wound Sizes', 'Wound Etiology'
        ];
        // CS-1344 - End
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 20;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(ivr => {
          const ivrInfo = { ...ivr } as IVRExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'IVR ID': //CS-1385 - Label Change
                return ivrInfo.ivrNumber;
              // CS-1344 - Start
              //CS-1625
              case 'Product':
                return ivrInfo.productName || 'N/A';
              case 'Manufacturer Name':
                return ivrInfo.manufacturerName || 'N/A';
              case 'Practice':
                return ivrInfo.practiceName || 'N/A';
              case 'IVR Submission Date':
                return ivrInfo.createdDate || 'N/A';
              case 'Date of Service':
                return ivrInfo.dateOfService || 'N/A';
              case 'IVR Status':
                return ivrInfo.verificationStatus || 'N/A';
              case 'Patient Date of Birth': {
                const [month, day, year] = ivrInfo.patientDOB.split('/');
                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
              }
              case 'Patient Name':
                return `${ivrInfo.patientFirstName || ''} ${ivrInfo.patientMiddleName || ''} ${ivrInfo.patientLastName || ''}`.trim() || 'N/A';
              case 'Patient ID': //CS-1385 - Label Change
                return ivrInfo.patientCode;
              // CS-1344 - End
              case 'Practice NPI': //CS-1385 - Label Change
                return ivrInfo.practiceNpi;
              case 'Provider NPI': //CS-1385 - Label Change
                return ivrInfo.practiceProviderNpi;
              case 'Primary Payer Company':
                return (ivrInfo.infoType === "PRIMARY_INSURANCE" && ivrInfo.patientinfoData?.insuranceName) || "N/A";
              case 'Primary Payer Plan Type':
                return (ivrInfo.infoType === "PRIMARY_INSURANCE" && ivrInfo.patientinfoData?.insuranceType) || "N/A";
              case 'Primary Payer Plan Policy ID':
                return (ivrInfo.infoType === "PRIMARY_INSURANCE" && ivrInfo.patientinfoData?.policy) || "N/A";
              case 'Primary Type':
                return (ivrInfo.infoType === "PRIMARY_INSURANCE" && ivrInfo.patientinfoData?.type) || "Insurance";
              case 'Secondary Payer Company':
                return (ivrInfo.infoType === "SECONDARY_INSURANCE" && ivrInfo.patientinfoData?.insuranceName) || "";
              case 'Secondary Payer Plan Type':
                return (ivrInfo.infoType === "SECONDARY_INSURANCE" && ivrInfo.patientinfoData?.insuranceType) || "";
              case 'Secondary Payer Plan Policy ID':
                return (ivrInfo.infoType === "SECONDARY_INSURANCE" && ivrInfo.patientinfoData?.policy) || "";
              case 'Secondary Type':
                return (ivrInfo.infoType === "SECONDARY_INSURANCE" && ivrInfo.patientinfoData?.type) ? "Insurance" : "";
              case 'Surgical Global Period':
                return ivrInfo.ivrInfo.treatmentInfo.currentlyUnderSurgicalGlobalPeriod;
              case 'Is in Skilled Nursing Home Facility':
                return ivrInfo.ivrInfo.treatmentInfo.currentlyInNursingFacility;
              case 'Passed 100 Days Mark in Skilled Nursing Home Facility':
                {
                  if (ivrInfo.ivrInfo.treatmentInfo.daysAdmittedToFacility &&
                    parseInt(ivrInfo.ivrInfo.treatmentInfo.daysAdmittedToFacility) > 100) {
                    return "True";
                  } else {
                    return "False"
                  }
                };
              case 'DX Codes':
                return ivrInfo.ivrInfo.woundInfo.woundData.length > 0
                  ? ivrInfo.ivrInfo.woundInfo.woundData
                    .map(wound => wound.otherDxCodes)
                    .filter(dxCode => dxCode && dxCode.trim() !== "")
                    .join(", ")
                  : "";
              case 'Place of Service':
                return ivrInfo.ivrInfo.treatmentInfo.placesOfService.length > 0
                  ? ivrInfo.ivrInfo.treatmentInfo.placesOfService
                    .filter(place => place && place.trim() !== "")
                    .join(", ")
                  : "";
              case 'Surgery Date':
                return ivrInfo.dateOfSurgery || 'N/A';
              case 'Wound Sizes':
                return ivrInfo.ivrInfo.woundInfo.woundData.length > 0
                  ? ivrInfo.ivrInfo.woundInfo.woundData
                    .map(wound => Math.trunc(Number(wound.totalSize)))
                    .join(", ")
                  : "";

              case 'Wound Etiology':
                return ivrInfo.ivrInfo.woundInfo.woundData.length > 0
                  ? ivrInfo.ivrInfo.woundInfo.woundData.map(wound => wound.woundType).join(", ")
                  : "";
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);

        });
        // CS-1156 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.AGENCY_LIST) {
        const headerRow = [
          'Agency Name', 'Phone', 'Address', 'Service Fee Type', 'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, indexe) => {
          worksheet.getColumn(indexe + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(agency => {
          const agencyInfo = agency as AgencyExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Agency Name':
                return agencyInfo.agencyName;
              case 'Phone':
                return agencyInfo.phone;
              case 'Address':
                return formatAddress(
                  agencyInfo.streetAddress,
                  agencyInfo.unitNumber,
                  agencyInfo.city,
                  agencyInfo.state,
                  agencyInfo.zipCode
                )
              case 'Service Fee Type':
                return getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, agencyInfo.commissionType) || '';
              case 'Status':
                return agencyInfo.inactiveDate ? 'INACTIVE' : 'ACTIVE';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
      } else if (sectionName === EXPORTABLE_ITEMS.AGENCY_DETAILS) {
        const headerRow = [
          'Agency Name', 'Service Fee Type', 'Email', 'Address', 'Practice Name', 'Product Info', 'Account Rep'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        allRecords.forEach(agency => {

          const agencyInfo = agency as any;

          const practiceNames = agencyInfo.agencyExportDataList
            ?.map((practice: any) => practice.practiceName)
            .join('\r\n─────────────────\r\n') || '';

          const accountReps = agencyInfo.agencyExportDataList
            ?.map((practice: any) => practice.accountRep)
            .join('\r\n─────────────────\r\n') || '';

          // Combine product info
          const productInfo = agencyInfo.agencyProductCommisions
            ?.map((product: any) => {
              return `Product: ${product.productName || ''}\n` +
                `Effective Date: ${product.effectiveDate || ''}\n` +
                `Service Rate: ${product.commissionRate ?? ''}%\n` +
                `Inactive Date: ${product.inactiveDate || 'N/A'}`;
            })
            .join('\r\n─────────────────\r\n') || '';

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Agency Name':
                return agencyInfo.agencyName;
              case 'Service Fee Type':
                return getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, agencyInfo.commissionType) || '';
              case 'Email':
                return agencyInfo.email;
              case 'Address':
                return formatAddress(
                  agencyInfo.streetAddress,
                  agencyInfo.unitNumber,
                  agencyInfo.city,
                  agencyInfo.state,
                  agencyInfo.zipCode
                );
              case 'Practice Name':
                return practiceNames;
              case 'Product Info':
                return productInfo;
              case 'Account Rep':
                return accountReps;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });

      } else if (sectionName === EXPORTABLE_ITEMS.AGENCY) {
        const headerRow = [
          'Agency Name', 'Phone', 'Email', 'Address', 'Service Fee Type',
          'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, indexe) => {
          worksheet.getColumn(indexe + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(agency => {
          const agencyInfo = agency as AgencyExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Agency Name':
                return agencyInfo.agencyName;
              case 'Phone':
                return agencyInfo.phone;
              case 'Email':
                return agencyInfo.email;
              case 'Address':
                return formatAddress(
                  agencyInfo.streetAddress,
                  agencyInfo.unitNumber,
                  agencyInfo.city,
                  agencyInfo.state,
                  agencyInfo.zipCode
                )
              case 'Service Fee Type':
                return getCodeTableDisplayLabel(allCodeTablesValuesMap, COMMISSION_TYPE_CODETABLE, agencyInfo.commissionType) || '';
              case 'Status':
                return agencyInfo.inactiveDate ? 'INACTIVE' : 'ACTIVE';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1156 - End
        // CS-1370 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PRACTICE) {
        const headerRow = [
          'Practice Name', 'Contact Name', 'Contact Email', 'Contact Phone Number',
          'Status', 'Created Date', 'Agency', 'Account Rep',
          'Location Address'  //  CS-1691
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(practice => {
          const practiceInfo = practice as PracticeExportInfo;

          // CS-1691 - Start
          const locations = (practiceInfo as any).practiceLocations;
          let locationRichText: any[] = [];
          if (locations && Array.isArray(locations) && locations.length > 0) {
            const locationList = locations.map((loc: any) => {
              const parts = [
                loc.streetAddress ?? '',
                loc.unitNumber ? (loc.unitNumber.trim() ? loc.unitNumber : '') : '',
                loc.city ?? '',
                loc.state ?? '',
                loc.zipCode ?? ''
              ].filter(p => p && p.toString().trim() !== '');
              return parts.join(', ');
            });

            // add Colours for the location text & separators
            locationList.forEach((location, index) => {
              locationRichText.push({
                text: location,
                font: { color: { argb: 'FF000000' } }
              });
              if (index < locationList.length - 1) {
                locationRichText.push({
                  text: '\r\n─────────────────\r\n',
                  font: { color: { argb: 'FFD3D3D3' } }
                });
              }
            });
          }

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice Name':
                return practiceInfo.practiceName || '';
              case 'Contact Name':
                return `${practiceInfo.title ? practiceInfo.title + ' ' : ''}${practiceInfo.contactFirstName || ''} ${practiceInfo.contactMiddleName ? practiceInfo.contactMiddleName + ' ' : ''}${practiceInfo.contactLastName || ''}`.trim();
              case 'Contact Email':
                return practiceInfo.contactEmail || '';
              case 'Contact Phone Number':
                return practiceInfo.contactPhone || '';
              case 'Status':
                return practiceInfo.approvalStatus || '';
              case 'Created Date':
                if (practiceInfo.requestTimestamp) {
                  const date = new Date(practiceInfo.requestTimestamp);
                  const year = date.getFullYear();
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  return `${month}/${day}/${year}`;
                }
                return '';
              case 'Agency':
                return practiceInfo.agencyName || '';
              case 'Account Rep':
                return Array.isArray(practiceInfo.executivesNames) ? practiceInfo.executivesNames.join(', ') : practiceInfo.executivesNames || 'N/A';
              case 'Location Address':
                return locationRichText.length > 0 ? { richText: locationRichText } : '';
            default:
              return '';
            }
          });
          
          const addedRow = worksheet.addRow(rowData);

          const locationColIndex = headerRow.indexOf('Location Address') + 1;
          if (locationColIndex > 0) {
            const cell = addedRow.getCell(locationColIndex);
            cell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' };
            cell.font = { size: 12 };
          }
        });// CS-1691 - End
        // CS-1370 - End
      } else if (sectionName === EXPORTABLE_ITEMS.PRACTICE_DETAILS) {
        const headerRow = [
          'Practice Name', 'Specialization', 'Contact Name', 'Contact Email', 'Contact Phone',
          'Authorized Person', 'Authorized Phone', 'Authorized Email', 'Contact Fax',
          'Practice PTAN', 'Practice NPI', 'Practice TAX ID', 'Billing Type', 'Providers', 'Account Rep'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(practice => {
          const practiceInfo = practice as PracticeExportInfo;
          const apFirstName = (practiceInfo as any).apFirstName || '';
          const apMiddleName = (practiceInfo as any).apMiddleName || '';
          const apLastName = (practiceInfo as any).apLastName || '';
          const practiceNameWithAP = `${practiceInfo.practiceName || ''}${apFirstName || apMiddleName || apLastName ? ` (${[apFirstName, apMiddleName, apLastName].filter(Boolean).join(' ')})` : ''}`;

          const innerPractice = Array.isArray((practiceInfo as any).practice)
            ? (practiceInfo as any).practice[0]
            : null;

          const providersList =
            Array.isArray(practiceInfo.practiceProviderNames)
              ? practiceInfo.practiceProviderNames.join(', ')
              : '';

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice Name':
                return practiceNameWithAP;
              case 'Specialization':
                return innerPractice?.specialization || '';
              case 'Contact Name':
                return `${(practiceInfo as any).title ? (practiceInfo as any).title + ' ' : ''}${practiceInfo.contactFirstName || ''} ${practiceInfo.contactMiddleName ? practiceInfo.contactMiddleName + ' ' : ''}${practiceInfo.contactLastName || ''}`.trim();
              case 'Contact Email':
                return practiceInfo.contactEmail || '';
              case 'Contact Phone':
                return practiceInfo.contactPhone || '';
              case 'Authorized Person':
                return [apFirstName, apMiddleName, apLastName].filter(Boolean).join(' ') || '';
              case 'Authorized Phone':
                return (practiceInfo as any).apPhone || '';
              case 'Authorized Email':
                return (practiceInfo as any).apEmail || '';
              case 'Contact Fax':
                return innerPractice?.contactFax || '';
              case 'Practice PTAN':
                return (practiceInfo as any).ptan || '';
              case 'Practice NPI':
                return (practiceInfo as any).npi || '';
              case 'Practice TAX ID':
                return innerPractice?.taxId || '';
              case 'Billing Type':
                return innerPractice?.billingType || '';
              case 'Providers':
                return providersList;
              case 'Account Rep':
                return Array.isArray(practiceInfo.executivesNames) ? practiceInfo.executivesNames.join(', ') : practiceInfo.executivesNames || 'N/A';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1405 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.AUDIT_LOG) {
        const headerRow = [
          'Audited Entity', 'Entity #', 'Action Type', 'User', 'Audit Message', 'User IP', 'Audited ON'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 25;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        (allRecords as AuditLogExportInfo[]).forEach((log) => {
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Audited Entity':
                return log.entityName;
              case 'Entity #':
                return log.entityNumber;
              case 'Action Type':
                return Array.isArray(log.actionType) ? log.actionType.join(', ') : log.actionType;
              case 'User':
                return log.userName;
              case 'Audit Message':
                return log.action;
              case 'User IP':
                return log.clientIP;
              case 'Audited ON':
                return log.auditTimestamp;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1405 - End
      } else if (sectionName === EXPORTABLE_ITEMS.AGREEMENT_LIST) {
        const headerRow = [
          'Product Name', 'Manufacturer', 'Practice', 'Provider', 'Effective Date', 
          'Inactive Date', 'File Type', 'Discount', 'Extended Price', 'Status', 'Time'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(productAgreement => {
          const productAgreementInfo = productAgreement as ProductAgreementExportInfo;
          const discountType = productAgreementInfo.hasSKUPrice ? "-" : productAgreementInfo.discountType !== "CUSTOM"
            ? `${productAgreementInfo.discountType} %` : productAgreementInfo.discountType;

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Product Name':
                return productAgreementInfo.productName;
              case 'Manufacturer':
                return productAgreementInfo.manufacturerName || '';
              case 'Practice':
                return productAgreementInfo.practiceName;
              case 'Provider':
                return (productAgreementInfo as any).providerName || '';
              case 'Effective Date':
                return productAgreementInfo.effectiveDate;
              case 'Inactive Date':
                return (productAgreementInfo as any).inactiveDate || '';
              case 'File Type':
                return (productAgreementInfo as any).fileType || '';
              case 'Discount':
                return discountType;
              case 'Extended Price':
                return productAgreementInfo.hasSKUPrice ? 'SKU Price' : Number(productAgreementInfo.extendedPrice);
              case 'Status':
                return productAgreementInfo.agreementStatus;
              case 'Time':
                return (productAgreementInfo as any).auditTimestamp || '';
              default:
                return '';
            }
          });

          const addedRow = worksheet.addRow(rowData);
          if (!productAgreementInfo.hasSKUPrice) {
            const extendedPriceColIndex = headerRow.indexOf('Extended Price') + 1;
            addedRow.getCell(extendedPriceColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          }
        });
      } else if (sectionName === EXPORTABLE_ITEMS.AGREEMENT) {
        const headerRow = [
          'Product Name', 'Practice Name', 'Contact Email', 'Contact Phone Number',
          'Effective Date', 'Agreement Status', 'Manufacturer Name', 'AP First Name',
          'AP Last Name', 'AP Phone Number', 'AP Email', 'Discount', 'Extended Price'
        ];
        worksheet.addRow(headerRow);

        const extendedPriceColIndex = headerRow.indexOf('Extended Price');
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = extendedPriceColIndex == index ? 80 : 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(productAgreement => {
          const productAgreementInfo = productAgreement as ProductAgreementExportInfo;

          const skuRows = productAgreementInfo?.skuProducts?.map((sku) => ({
            productItemSku: sku.productItemSku,
            productItemDescription: sku.productItemDescription,
            discountType: sku.discountType !== "CUSTOM" ? `${sku.discountType} %` : sku.discountType,
            extendedPrice: priceFormatter(sku.extendedPrice)
          }));

          const discountType = productAgreementInfo.hasSKUPrice ? "-" : productAgreementInfo.discountType !== "CUSTOM"
            ? `${productAgreementInfo.discountType} %` : productAgreementInfo.discountType;

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Product Name':
                return productAgreementInfo.productName;
              case 'Practice Name':
                return productAgreementInfo.practiceName;
              case 'Contact Email':
                return productAgreementInfo.contactEmail;
              case 'Contact Phone Number':
                return productAgreementInfo.contactPhone;
              case 'Effective Date':
                return productAgreementInfo.effectiveDate;
              case 'Agreement Status':
                return productAgreementInfo.agreementStatus;
              case 'Manufacturer Name':
                return productAgreementInfo.manufacturerName;
              case 'AP First Name':
                return productAgreementInfo.apFirstName;
              case 'AP Last Name':
                return productAgreementInfo.apLastName;
              case 'AP Phone Number':
                return productAgreementInfo.apPhone;
              case 'AP Email':
                return productAgreementInfo.apEmail;
              case 'Discount':
                return discountType;
              case 'Extended Price':
                if (productAgreementInfo.hasSKUPrice && skuRows && skuRows.length > 0) {
                  let tableText = 'SKU | Description | Discount | Price\n';
                  tableText += '─'.repeat(60) + '\n';
                  skuRows.forEach(sku => {
                    tableText += `${sku.productItemSku} | ${sku.productItemDescription ? sku.productItemDescription : "  "} | ${sku.discountType} | ${sku.extendedPrice}\n`;
                  });
                  return tableText;
                } else {
                  return Number(productAgreementInfo.extendedPrice);
                }
              default:
                return '';
            }
          });

          const addedRow = worksheet.addRow(rowData);

          if (productAgreementInfo.hasSKUPrice && skuRows && skuRows.length > 0) {
            const extendedPriceCell = addedRow.getCell(extendedPriceColIndex + 1);
            extendedPriceCell.alignment = {
              wrapText: true,
              horizontal: 'left',
              vertical: 'top'
            };
            extendedPriceCell.font = {
              name: 'Courier New',
              size: 10
            };
          } else {
            const extendedPriceCell = addedRow.getCell(extendedPriceColIndex + 1);
            extendedPriceCell.numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          }
        });
      } else if (sectionName === EXPORTABLE_ITEMS.PAYMENT) {
        const headerRow = [
          'INV ID', 'Order ID','Transaction ID', 'Practice Name', 'Manufacturer', 'Pay Method', 'INV Total', 'Paid Amt', 'INV Date', 'Pay Date',
          'Agency name', 'Account Rep Name(s)', 'Pay Type', 'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, indexe) => {
          worksheet.getColumn(indexe + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };
        allRecords.forEach(payment => {
          const paymentInfo = payment as PaymentInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'INV ID':
                return paymentInfo.invoiceNumber;
              case 'Order ID':
                return paymentInfo.orderNumber;
              case 'Transaction ID':
                return paymentInfo.transactionId;
              case 'Practice Name':
                return paymentInfo.practiceName;
              case 'Manufacturer':
                return paymentInfo.manufacturerName;
              case 'Pay Method':
                return formatPaymentMethod(paymentInfo.paymentMethod);
              case 'INV Total':
                return Number(paymentInfo.invoiceAmount);
              case 'Paid Amt':
                return Number(paymentInfo.paymentAmount);
              case 'INV Date':
                return paymentInfo.invoiceDate;
              case 'Pay Date':
                return paymentInfo.paymentDate;
              case 'Agency name':
                return paymentInfo.agencyName;
              case 'Account Rep Name(s)':
                return paymentInfo.accountRepNames.join(", ") || "N/A";
              case 'Pay Type':
                return paymentInfo.paymentType;
              case 'Status':
                return paymentInfo.paymentStatus;
              default:
                return '';
            }
          });
          const addedRow = worksheet.addRow(rowData);
          const invTotalIndex = headerRow.indexOf('INV Total') + 1;
          const paidAmtIndex = headerRow.indexOf('Paid Amt') + 1;

          if (invTotalIndex > 0) {
            addedRow.getCell(invTotalIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          }
          if (paidAmtIndex > 0) {
            addedRow.getCell(paidAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
          }
        });
        // CS-1685 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.MANUFACTURER) {
        const headerRow = [
          'Manufacturer Name', 'Contact Number', 'Inactive Date', 'Legal Name'
        ]
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(manufacturer => {
          const manufacturerInfo = manufacturer as ManufacturerExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Manufacturer Name':
                return manufacturerInfo.manufacturerName;
              case 'Contact Number':
                return manufacturerInfo.contactPhone;
              case 'Inactive Date':
                return manufacturerInfo.inactiveDate ? manufacturerInfo.inactiveDate : 'N/A';
              case 'Legal Name': 
                return manufacturerInfo.legalName ? manufacturerInfo.legalName : 'N/A';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });

      } else if (sectionName === EXPORTABLE_ITEMS.MANUFACTURER_DETAILS) {
        const headerRow = [
          'Phone', 'v3pay day', 'v3 pay month', 'agency pay day', 'inactive date', 'IVR info', 'Order info', 'Return Info', 'Tissue info', 'Product agreement info',
          'invoice due by', 'invoice due days', 'send email to authorized practice signer', 'provide signature required', 'Template Level', 'Stripe Configurations - Y/N', 'NetSuite Configurations: Y/N'
        ]
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(manufacturer => {
          const manufacturerInfo = manufacturer as any;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Phone':
                return manufacturerInfo.contactPhone;
              case 'v3pay day':
                return manufacturerInfo.v3PayDay;
              case 'v3 pay month':
                return manufacturerInfo.v3PayMonth;
              case 'agency pay day':
                return manufacturerInfo.agencyPayDay;
              case 'inactive date':
                return manufacturerInfo.inactiveDate;
              case 'IVR info':
                return manufacturerInfo.notificationEmail?.ivr_info?.join('\n') ?? '';
              case 'Order info':
                return manufacturerInfo.notificationEmail?.order_info?.join('\n') ?? '';
              case 'Return Info':
                return manufacturerInfo.notificationEmail?.return_info?.join('\n') ?? '';
              case 'Tissue info':
                return manufacturerInfo.notificationEmail?.tissue_info?.join('\n') ?? '';
              case 'Product agreement info':
                return manufacturerInfo.notificationEmail?.product_agreement_info?.join('\n') ?? '';
              case 'invoice due by':
                return manufacturerInfo.customConfig?.invoice_due_date ?? '';
              case 'invoice due days':
                return manufacturerInfo.customConfig?.invoice_net_days ?? '';
              case 'send email to authorized practice signer':
                return manufacturerInfo.customConfig?.cc_authorized_practice_user === 'Y' ? "Yes" : "No";
              case 'provide signature required':
                return manufacturerInfo.customConfig?.provider_signature_required === 'Y' ? "Yes" : "No";
              case 'Template Level':
                return manufacturerInfo.customConfig?.template_level ?? '';
              case 'Stripe Configurations - Y/N':
                return manufacturerInfo.stripeConfig?.stripe_integration === 'Y' ? "Yes" : "No";
              case 'NetSuite Configurations: Y/N':
                return manufacturerInfo.netsuitConfig?.netsuite_integration === 'Y' ? "Yes" : "No";
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });


        // CS-1700 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PATIENTS) {

        const headerRow = [
          'Patient ID', 'Patient Name', 'Practice Name', 'DOB', 'Date of Surgery', 'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(patient => {
          const patientInfo = patient as any;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Patient ID':
                return patientInfo.patientCode;
              case 'Patient Name':
                return fullName(null, patientInfo.firstName, patientInfo.middleName, patientInfo.lastName);
              case 'Practice Name':
                return patientInfo.practiceName;
              case 'DOB':
                return patientInfo.birthDate || patientInfo.dob || '';
              case 'Date of Surgery':
                return patientInfo.dateOfSurgery || '';
              case 'Status':
                return patientInfo.status || '';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });

      } else if (sectionName === EXPORTABLE_ITEMS.PATIENT_DETAILS) {
        const headerRow = [
          'Patient Name', 'Patient Date of Birth', 'Gender', 'Patient\'s Address',
          'Date of Surgery', 'Wound info', 'Primary Insurance', 'Status', 'IVR List', 'Order List'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(patientDetail => {
          const detailInfo = patientDetail as any;
          console.log('detailInfo', detailInfo);
          const patientName = fullName(null, detailInfo.firstName, detailInfo.middleName, detailInfo.lastName);

          const addressInfo = detailInfo.addressInfo ||
            detailInfo.patientInfo?.find((p: any) => p.infoType === 'ADDRESS')?.patientinfoData;

          const patientAddress = formatAddress(
            addressInfo?.patientStreetAddress,
            addressInfo?.patientUnitNumber,
            addressInfo?.patientCity,
            addressInfo?.patientState,
            addressInfo?.patientZip
          );

          const woundInfoData = detailInfo.patientInfo?.find((p: any) => p.infoType === 'WOUND_INFO')?.patientinfoData?.woundInfo;
          const woundInfo = woundInfoData?.woundData || [];

          const woundInfoText = woundInfo.map((w: any) =>
            `Type: ${w.woundType || ''}, Size: ${w.totalSize || ''}, DX Codes: ${w.otherDxCodes || ''}`
          ).join('\r\n─────────────────\r\n');

          const primaryInsurance = detailInfo.patientInfo?.find((p: any) => p.infoType === 'PRIMARY_INSURANCE');
          const primaryInsuranceText = primaryInsurance?.patientinfoData 
            ? `${primaryInsurance.patientinfoData.insuranceName || ''} - ${primaryInsurance.patientinfoData.policy || ''}`
            : '';

          const orderList = (detailInfo.orders || [])
            .map((order: any) => `${order.orderNumber || ''} | ${order.productName || ''}`)
            .filter(Boolean)
            .join('\r\n─────────────────\r\n') || '';

          const ivrList = (detailInfo.ivrList || [])
            .map((ivr: any) => `${ivr.ivrNumber || ''} | ${ivr.productName || ''} | ${ivr.providerName || ''}`)
            .filter(Boolean)
            .join('\r\n─────────────────\r\n') || '';

          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Patient Name':
                return patientName;
              case 'Patient Date of Birth':
                return detailInfo.birthDate || detailInfo.dob || '';
              case 'Gender':
                return detailInfo.gender || '';
              case 'Patient\'s Address':
                return patientAddress;
              case 'Date of Surgery':
                return detailInfo.dateOfSurgery || '';
              case 'Wound info':
                return woundInfoText;
              case 'Primary Insurance':
                return primaryInsuranceText;
              case 'Status':
                return detailInfo.status || '';
              case 'IVR List':
                return ivrList;
              case 'Order List':
                return orderList;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });

      } else if (sectionName === EXPORTABLE_ITEMS.PROVIDER) {
        const headerRow = [
          'Practice', 'Provider Name', 'NPI', 'Ptan', 'Tax ID', 'Email', 'User Account Created'
        ]
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(provider => {
          const providerInfo = provider as ProviderExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice':
                return providerInfo.practiceName;
              case 'Provider Name':
                return fullName(providerInfo.title, providerInfo.firstName, providerInfo.middleName, providerInfo.lastName);
              case 'NPI':
                return providerInfo.npi;
              case 'Ptan': 
                return providerInfo.ptan;
              case 'Tax ID':
                return providerInfo.taxId || 'N/A';
              case 'Email':
                return providerInfo.email;
              case 'User Account Created':
                return providerInfo.hasUserAccount === true ? 'Yes' : providerInfo.approvedStatus !== 'APPROVED' ? "No (Practice not approved)" : "No";
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })
        // CS-1685 - End
      } else if (sectionName === "USER") {
        const headerRow = [
          'Email', 'User Type', 'Name', 'Phone', 'Last Login Time', 'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 25;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(user => {
          const userInfo = user as UserExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Email':
                return userInfo.email;
              case 'User Type':
                return userInfo.userRole;
              case 'Name':
                return userInfo.userName;
              case 'Phone':
                return userInfo.phone;
              case 'Last Login Time':
                return userInfo.lastLoginAt?.replace('T', ' ') || '';
              case 'Status':
                return userInfo.userStatus?.toUpperCase();
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
      } else if (sectionName === EXPORTABLE_ITEMS.USER_DETAILS) {
        const headerRow = [
          'User Type', 'Practice Name', 'Full Name', 'Email', 'Phone', 'Last Login Time', 'Status'
        ];
        worksheet.addRow(headerRow);

        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 25;
        });

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(user => {
          const userInfo = user as UserExportInfo;
          const fullName = `${userInfo.firstName || ''} ${userInfo.middleName || ''} ${userInfo.lastName || ''}`.trim();
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'User Type':
                return userInfo.userRole || '';
              case 'Practice Name':
                return (userInfo as any).practiceName || '';
              case 'Full Name':
                return fullName || userInfo.userName || '';
              case 'Email':
                return userInfo.email || '';
              case 'Phone':
                return userInfo.phone || '';
              case 'Last Login Time':
                return userInfo.lastLoginAt?.replace('T', ' ') || '';
              case 'Status':
                return userInfo.userStatus?.toUpperCase() || '';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1685 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PRODUCTS) {
        const headerRow = [
          'Product Name', 'Product Description', 'Manufacturer', 'Product Category', 'HCPCS Code', 'Status'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(product => {
          const productInfo = product as ProductExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Product Name':
                return productInfo.productName;
              case 'Product Description':
                return productInfo.productDescription;
              case 'Manufacturer':
                return productInfo.manufacturer.manufacturerName;
              case 'Product Category':
                return getCategoryDisplayLabel(allCodeTablesValuesMap, productInfo.productCategory);
              case 'HCPCS Code':
                return productInfo.hcpcsCode;
              case 'Status':
                return productInfo.productStatus;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })
      } else if (sectionName === EXPORTABLE_ITEMS.PRODUCTS_DETAILS) {
        const headerRow = [
          'Product Name', 'Product Description', 'Insurance Coverage', 'Manufacturer', 'Product Category', 
          'SKU', 'HCPCS Code', 'Size', 'Number of Units', 'Availability', 'Product List Price', 'Product Custom Price', 'Item List Price', 'Item Custom Price', 'Status'
        ];

        const productLevelCols = [
          'Product Name', 'Product Description', 'Insurance Coverage', 'Manufacturer', 'Product Category', 
          'Product List Price', 'Product Custom Price', 'Status'
        ];

        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 25;
        });
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

        allRecords.forEach(product => {
          const productInfo = product as ProductExportInfo;
          const productItems = productInfo.productItems || [];
          const prodPrice = Array.isArray(productInfo.productPrices) ? productInfo.productPrices : [productInfo.productPrices];

          if (productItems.length > 0) {
            // Create item rows for merging
            const itemRows: any[] = [];

            productItems.forEach((item: any) => {
              const itemPrice = item.productItemPrices?.[0] || {};
              const itemRow = new Array(headerRow.length).fill('');

              itemRow[headerRow.indexOf('SKU')] = `${productInfo.productName} - ${item.productItemSku || item.sku || ''}`;
              itemRow[headerRow.indexOf('HCPCS Code')] = item.hcpcsCode || productInfo.hcpcsCode || '';
              itemRow[headerRow.indexOf('Size')] = item.size || '';
              itemRow[headerRow.indexOf('Number of Units')] = item.units || '';
              itemRow[headerRow.indexOf('Availability')] = item.inactiveDate ? 'Inactive' : 'Available';
              itemRow[headerRow.indexOf('Item List Price')] = itemPrice.listPrice || 'N/A';
              itemRow[headerRow.indexOf('Item Custom Price')] = itemPrice.customPrice || 'N/A';

              itemRows.push(itemRow);
            });

            const startRow = worksheet.lastRow ? worksheet.lastRow.number + 1 : 2;

            itemRows.forEach((itemRow, idx) => {
              const finalRow = [...itemRow];
              if (idx === 0) {
                finalRow[headerRow.indexOf('Product Name')] = productInfo.productName || '';
                finalRow[headerRow.indexOf('Product Description')] = productInfo.productDescription || '';
                finalRow[headerRow.indexOf('Insurance Coverage')] = productInfo.insuranceCoverage || (productInfo as any).insuranceCoverage || '';
                finalRow[headerRow.indexOf('Manufacturer')] = productInfo.manufacturer?.manufacturerName || '';
                finalRow[headerRow.indexOf('Product Category')] = getCategoryDisplayLabel(allCodeTablesValuesMap, productInfo.productCategory);
                finalRow[headerRow.indexOf('Product List Price')] = prodPrice?.[0]?.listPrice ?? 'N/A';
                finalRow[headerRow.indexOf('Product Custom Price')] = prodPrice?.[0]?.customPrice ?? 'N/A';
                finalRow[headerRow.indexOf('Status')] = productInfo.productStatus || '';
              }
              worksheet.addRow(finalRow);
            });

            const endRow = worksheet.lastRow!.number;

            if (itemRows.length > 1) {
              headerRow.forEach((label, colIndex) => {
                if (productLevelCols.includes(label)) {
                  worksheet.mergeCells(startRow, colIndex + 1, endRow, colIndex + 1);
                }
              });
            }
          } else {
            const rowData = headerRow.map(header => {
              switch (header) {
                case 'Product Name':
                  return productInfo.productName || '';
                case 'Product Description':
                  return productInfo.productDescription || '';
                case 'Insurance Coverage':
                  return productInfo.insuranceCoverage || (productInfo as any).insuranceCoverage || '';
                case 'Manufacturer':
                  return productInfo.manufacturer?.manufacturerName || '';
                case 'Product Category':
                  return getCategoryDisplayLabel(allCodeTablesValuesMap, productInfo.productCategory);
                case 'Product List Price':
                  return prodPrice?.[0]?.listPrice ?? 'N/A';
                case 'Product Custom Price':
                  return prodPrice?.[0]?.customPrice ?? 'N/A';
                case 'SKU':
                  return '';
                case 'HCPCS Code':
                  return productInfo.hcpcsCode || '';
                case 'Size':
                  return '';
                case 'Number of Units':
                  return '';
                case 'Availability':
                  return '';
                case 'Item List Price':
                  return 'N/A';
                case 'Item Custom Price':
                  return 'N/A';
                case 'Status':
                  return productInfo.productStatus || '';
                default:
                  return '';
              }
            });
            worksheet.addRow(rowData);
          }
        });
      } else if (sectionName === EXPORTABLE_ITEMS.BAA) {
        const headerRow = [
          'Email', 'User Type', 'Practice', 'Inactive Date', 'Agreement Type', 'Signed Date', 'Status'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };
        
        allRecords.forEach(baa => {
          const baaInfo = baa as BaaExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Email':
                return baaInfo.email;
              case 'User Type':
                return baaInfo.userType;
              case 'Practice':
                return baaInfo.practiceName;
              case 'Inactive Date':
                return baaInfo.inactiveDate;
              case 'Agreement Type':
                return baaInfo.agreementType;
              case 'Signed Date':
                return baaInfo.userSignedDate;
              case 'Status':
                return baaInfo.agreementStatus;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })
        // CS-1685 - End
        // CS-1679 Start
      } else if (sectionName === "activeUsers") {

        const headerRow = [
          'Full Name', 'User Role', 'Last Activity'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };
        
        allRecords.forEach(activeUsers => {
          const activeUsersInfo = activeUsers as UserWithRoleDTO;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Full Name':
                return fullName(null, activeUsersInfo.firstName, activeUsersInfo.middleName, activeUsersInfo.lastName);
              case 'User Role':
                return activeUsersInfo.roleDisplayValue;
              case 'Last Activity':
                return formatDateTime(activeUsersInfo.lastLoginAt);
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })

      } else if (sectionName === "activePractices") {

        const headerRow = [
          'Practice Name', '# of IVRs', '# of Orders'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(activePractice => {
          const activePracticesInfo = activePractice as ActivePracticesDTO;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice Name':
                return activePracticesInfo.practiceName;
              case '# of IVRs':
                return activePracticesInfo.ivrCount;
              case '# of Orders':
                return activePracticesInfo.orderCount;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })

      } else if (sectionName === "totalPatients") {

        const headerRow = [
          'Full Name', 'Associated Practice'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(patient => {
          const activePatientInfo = patient as PatientInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Full Name':
                return fullName(null, activePatientInfo.firstName, activePatientInfo.middleName, activePatientInfo.lastName);
              case 'Associated Practice':
                return activePatientInfo.practiceName;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })

      } else if (sectionName === "weeklySales") {

        const headerRow = [
          'Practice Name', 'Total Sales Amount', '# of Orders', '% of Total'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(sales => {
          const activeSalesInfo = sales as PracticeSalesSummaryDTO;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice Name':
                return activeSalesInfo.practiceName;
              case 'Total Sales Amount':
                return priceFormatter(activeSalesInfo.totalSalesAmount);
              case '# of Orders':
                return activeSalesInfo.numberOfOrders;
              case '% of Total':
                return activeSalesInfo.percentageOfTotalSales;
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })

      } else if (sectionName === "weeklyOrders") {

        const headerRow = [
          'Practice Name', 'Total Orders', 'Total Order Value'
        ]
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30
        })
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach(order => {
          const ordersInfo = order as PracticeSalesSummaryDTO;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Practice Name':
                return ordersInfo.practiceName;
              case 'Total Orders':
                return ordersInfo.numberOfOrders;
              case 'Total Order Value':
                return priceFormatter(ordersInfo.totalSalesAmount);
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        })
        // CS-1679 End
      // CS-1706 START - Service Subscription Export
      } else if (sectionName === EXPORTABLE_ITEMS.SERVICE_SUBSCRIPTION) {
        const headerRow = [
          'User Name', 'Email', 'Sign Date', 'Status'
          // 'Service Type'
        ];
        worksheet.addRow(headerRow);
        
        headerRow.forEach((_, index) => {
          worksheet.getColumn(index + 1).width = 30;
        });
        
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };
        
        allRecords.forEach(subscription => {
          const subscriptionInfo = subscription as ServiceSubscriptionExportInfo;
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'User Name':
                return subscriptionInfo.userName || '';
              case 'Email':
                return subscriptionInfo.userEmail || '';
              // case 'Service Type':
              //   return subscriptionInfo.serviceName || '';
              case 'Sign Date':
                return subscriptionInfo.signDatetime || '';
              case 'Status':
                return subscriptionInfo.status || '';
              default:
                return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1706 END
        // CS-1728 START - Explore Market worksheet
      } else if (sectionName === EXPORTABLE_ITEMS.EXPLORE_MARKET) {
        const headerRow = [
          'Product Name', 'Manufacturer', 'Product Category', 'Composition',
          'HCPCS Code', 'Allowable Rate', 'Extended Price', 'Agreement Status', 'Status',
          'Purchase & Bill' // CS-1765
        ];
        worksheet.addRow(headerRow);
        headerRow.forEach((_, index) => { worksheet.getColumn(index + 1).width = 25; });
        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' };
        headerRowCells.alignment = { horizontal: 'center', vertical: 'middle' };

        allRecords.forEach((record: any) => {
          const rowData = headerRow.map(header => {
            switch (header) {
              case 'Product Name': return record.productName ?? '';
              case 'Manufacturer': return record.manufacturerName ?? '';
              case 'Product Category': return getCategoryDisplayLabel(allCodeTablesValuesMap, record.productCategory);
              case 'Composition': return record.composition ?? '';
              case 'HCPCS Code': return record.hcpcsCode ?? '';
              case 'Allowable Rate': return record.allowableRate != null ? record.allowableRate : '';
              case 'Extended Price': return record.extendedPrice != null ? record.extendedPrice : '';
              case 'Agreement Status': return record.agreementStatus ?? '';
              case 'Status': return record.productStatus ?? '';
              case 'Purchase & Bill': return record.purchaseAndBill === 'Y' ? 'Yes' : record.purchaseAndBill === 'N' ? 'No' : '';
              default: return '';
            }
          });
          worksheet.addRow(rowData);
        });
        // CS-1728 END
      } else {
        const visibleColumns = formattedColumns.filter((column: Column, index: any) =>
          !hiddenColumns?.includes(column.id) &&
          !(index === 0 &&
            (typeof column.label !== 'string' || column.label.trim() === '')
          ) &&
          !(index == formattedColumns.length - 1 && column.label == 'Action')
        );
        const headerRow = visibleColumns.map((column: Column) =>
          typeof column.label === 'string' ? column.label : column.id
        );

        let commissionSpecificCols: string[] = [];
        const v3Export = EXCEL_FINANCE_HQ_V3_EXPORT_COLUMNS;

        if (sectionName === EXPORTABLE_ITEMS.COMMISSION) {
          if (!headerRow.includes("INV Reference ID")) {
            const practiceIndex = visibleColumns.findIndex((column: Column) =>
              column.id === "practiceName" || column.label === "Practice"
            );
            if (practiceIndex >= 0) {
              headerRow.splice(practiceIndex + 1, 0, "INV Reference ID");
            } else {
              headerRow.push("INV Reference ID");
            }
          }

          if (!headerRow.includes("NET INV Amt")) {
            const invAmtIndex = visibleColumns.findIndex((column: Column) =>
              column.id === "invoiceAmount" || column.label === "INV Amt"
            );
            if (invAmtIndex >= 0) {
              headerRow.splice(invAmtIndex + 1, 0, "NET INV Amt");
            } else {
              headerRow.push("NET INV Amt");
            }
          }

          const financeHqInvoiceCols: string[] = ["Product Units"];
          const userRole = store.getState()?.authSlice?.userInfo?.userRole;
          const isSuperAdmin = userRole === USER_ROLES.ROLE_SUPERADMIN;
          const includeProfitColumns = isSuperAdmin || (allRecords as any[]).some((record) =>
            record?.gpAmount != null || record?.cogsTotal != null || record?.gpPreCosAmount != null
          );
          if (includeProfitColumns) {
            financeHqInvoiceCols.push(
              "COGs $ P/U",
              "COGs $ Total",
              "GP $",
              "GP $ P/U",
              "GP $ Pre-COS",
              "GP $ Pre-COS P/U"
            );
          }
          headerRow.push(...financeHqInvoiceCols);

          commissionSpecificCols = [
            "Payment Amount", "NET INV PMT Amt", "Payment Date",
            v3Export.SERVICE_FEE_PERCENT,
            v3Export.SERVICE_FEE_AMOUNT,
            v3Export.PAY_STATUS,
            v3Export.CLEARED_RECEIVED_DATE,
            "Agency %", "Agency Svc Fee $", "Agency Pay Status", "Agency Service Fee Paid Date",
            "Override %", "Override $", "OVR Pay Status", "Override Service Fee Paid Date"
          ];

          headerRow.push(...commissionSpecificCols);
        }

        worksheet.addRow(headerRow);

        const headerRowCells = worksheet.getRow(1);
        headerRowCells.font = { bold: true, color: { argb: 'FF000000' }, size: 15, underline: 'single' }
        headerRowCells.alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
        headerRow.forEach((label: any, index: any) => {
          const column = worksheet.getColumn(index + 1);
          column.width = 20;
        });

        const getInvoiceAgingDisplayValue = (data: any): string | number => {
          const isPaidOrClosed = data?.invoiceStatus === "PAID" || data?.invoiceStatus === "CLOSED";
          const invoiceLateDays = data?.invoiceLateDays;

          if(data?.invoiceCountDays === 60 && !isPaidOrClosed) {

            if (invoiceLateDays > 60) {
              return invoiceLateDays;
            }
            if (invoiceLateDays >= 31 && invoiceLateDays <= 60) {
              return invoiceLateDays;
            }
            if (invoiceLateDays >= 1 && invoiceLateDays <= 30) {
              return invoiceLateDays;
            }
            if (invoiceLateDays <= 0) {
              return invoiceLateDays;
            }

          } else if (data?.invoiceCountDays === 45 && !isPaidOrClosed) {

            if(invoiceLateDays > 45) {
              return invoiceLateDays;
            }
            if (invoiceLateDays >= 16 && invoiceLateDays <= 45) {
              return invoiceLateDays;
            }
            if (invoiceLateDays >= 1 && invoiceLateDays <= 15) {
              return invoiceLateDays;
            }
            if (invoiceLateDays <= 0) {
              return invoiceLateDays;
            }

          } else if (data?.invoiceCountDays === 30 && !isPaidOrClosed) {
            if (invoiceLateDays > 30) {
              return invoiceLateDays;
            }
            if (invoiceLateDays > 9 && invoiceLateDays <= 30) {
              return invoiceLateDays;
            }
            if (invoiceLateDays >= 1 && invoiceLateDays <= 9) {
              return invoiceLateDays;
            }
            if (invoiceLateDays <= 0) {
              return invoiceLateDays;
            }
          } else {
            return '-';
          }
          
          // fallback method
          return '-';
        };

        allRecords.forEach(row => {
          const rowData = headerRow.map((label: String) => {
            if (sectionName === EXPORTABLE_ITEMS.COMMISSION && label === "INV Reference ID") {
              return (row as invoiceTableRow).invReferenceId ?? "";
            }
            if (sectionName === EXPORTABLE_ITEMS.COMMISSION && label === "NET INV Amt") {
              const netInvoiceAmount = (row as invoiceTableRow).netInvoiceAmount;
              return netInvoiceAmount != null && netInvoiceAmount !== "" ? Number(netInvoiceAmount) : netInvoiceAmount;
            }
            if (sectionName === EXPORTABLE_ITEMS.COMMISSION) {
              const financeRow = row as invoiceTableRow & {
                productUnits?: number | null;
                cogsPerUnit?: number | null;
                cogsTotal?: number | null;
                gpAmount?: number | null;
                gpPerUnit?: number | null;
                gpPreCosAmount?: number | null;
                gpPreCosPerUnit?: number | null;
              };
              if (label === "Product Units") {
                return financeRow.productUnits != null ? Number(financeRow.productUnits) : "";
              }
              if (label === "COGs $ P/U") {
                return financeRow.cogsPerUnit != null ? Number(financeRow.cogsPerUnit) : "";
              }
              if (label === "COGs $ Total") {
                return financeRow.cogsTotal != null ? Number(financeRow.cogsTotal) : "";
              }
              if (label === "GP $") {
                return financeRow.gpAmount != null ? Number(financeRow.gpAmount) : "";
              }
              if (label === "GP $ P/U") {
                return financeRow.gpPerUnit != null ? Number(financeRow.gpPerUnit) : "";
              }
              if (label === "GP $ Pre-COS") {
                return financeRow.gpPreCosAmount != null ? Number(financeRow.gpPreCosAmount) : "";
              }
              if (label === "GP $ Pre-COS P/U") {
                return financeRow.gpPreCosPerUnit != null ? Number(financeRow.gpPreCosPerUnit) : "";
              }
            }

            const column = formattedColumns.find((col: Column) => col.label === label);

            if (column) {
              if (sectionName === EXPORTABLE_ITEMS.INVOICE || sectionName === EXPORTABLE_ITEMS.COMMISSION) {
                const value = (row as invoiceTableRow)[column.id as keyof invoiceTableRow];

                if (label === "Invoice Total" || label === "Outstanding Amt" || label === "INV Amt" || label === "NET INV Amt") {
                  return value ? Number(value) : value;
                }

                if (label === "INV Aging") {
                  return getInvoiceAgingDisplayValue(row);
                }

                return value;
              }
            }

            return '';
          });

          if (sectionName === EXPORTABLE_ITEMS.COMMISSION) {
            const commissionRows: any[] = [];

            // CS-2104: OVERRIDE download rows often omit commissionStatus. Prefer payment status,
            // then invoice-level ovrCommStatus, then paid-vs-expected (UNPAID/PAID like Agency).
            const resolveOverridePayStatus = (commission: any, invoiceRow: any) => {
              if (commission?.commissionStatus) {
                return commission.commissionStatus;
              }
              if (invoiceRow?.ovrCommStatus) {
                return invoiceRow.ovrCommStatus;
              }
              const expected = Number(commission?.expectedCommissionAmount) || 0;
              const paid = Number(commission?.calculatedCommissionAmount) || 0;
              if (expected <= 0) {
                return "";
              }
              return paid >= expected ? "PAID" : "UNPAID";
            };

            const invoiceRow = row as invoiceTableRow & {
              purchaseAndBill?: string | null;
              manufactureCustomConfig?: { purchase_and_bill?: string };
            };
            const purchaseAndBillFlag = String(
              invoiceRow.purchaseAndBill
                ?? invoiceRow.customConfig?.purchase_and_bill
                ?? invoiceRow.manufactureCustomConfig?.purchase_and_bill
                ?? ""
            ).toUpperCase();
            const isPurchaseAndBill = purchaseAndBillFlag === "Y" || purchaseAndBillFlag === "YES";
            const isInvoicePaid = String(invoiceRow.invoiceStatus || "").toUpperCase() === "PAID";

            (row as invoiceTableRow).payments?.forEach((payment: any) => {
              const commissionRow = new Array(headerRow.length).fill('');
              //CS-1620 - Standardize Currency Formatting
              commissionRow[headerRow.indexOf("Payment Amount")] = Number(payment.paymentAmount);
              const netPaymentAmount = payment.adjustedAmount != null ? payment.adjustedAmount : payment.paymentAmount;
              commissionRow[headerRow.indexOf("NET INV PMT Amt")] = Number(netPaymentAmount);
              // commissionRow[headerRow.indexOf("Payment Amount")] = priceFormatter(Number(payment.paymentAmount));
              commissionRow[headerRow.indexOf("Payment Date")] = payment.paymentDate;

              payment.commissions?.forEach((commission: any) => {
                if (isV3CommissionType(allCodeTablesValuesMap, commission.agencyType)) {
                  commissionRow[headerRow.indexOf(v3Export.SERVICE_FEE_PERCENT)] = commission.expectedCommissionRate;
                  commissionRow[headerRow.indexOf(v3Export.SERVICE_FEE_AMOUNT)] = Number(commission.expectedCommissionAmount);
                  commissionRow[headerRow.indexOf(v3Export.PAY_STATUS)] = commission.commissionStatus;
                  commissionRow[headerRow.indexOf(v3Export.CLEARED_RECEIVED_DATE)] = commission.commissionReceivedDate;
                }
                if (commission.agencyType === "AGENCY") {
                  commissionRow[headerRow.indexOf("Agency %")] = commission.expectedCommissionRate;
                  commissionRow[headerRow.indexOf("Agency Svc Fee $")] = Number(commission.expectedCommissionAmount);
                  // commissionRow[headerRow.indexOf("Agency Svc Fee $")] = priceFormatter(Number(commission.expectedCommissionAmount));
                  commissionRow[headerRow.indexOf("Agency Pay Status")] = commission.commissionStatus;
                  commissionRow[headerRow.indexOf("Agency Service Fee Paid Date")] = commission.commissionReceivedDate;
                }
                if (commission.agencyType === "OVERRIDE") {
                  commissionRow[headerRow.indexOf("Override %")] = commission.expectedCommissionRate;
                  commissionRow[headerRow.indexOf("Override $")] = Number(commission.expectedCommissionAmount);
                  // commissionRow[headerRow.indexOf("Override $")] = priceFormatter(Number(commission.expectedCommissionAmount));
                  // commissionRow[headerRow.indexOf("OVR Pay Status")] = commission.commissionStatus;
                  commissionRow[headerRow.indexOf("OVR Pay Status")] = resolveOverridePayStatus(commission, row);
                  commissionRow[headerRow.indexOf("Override Service Fee Paid Date")] = commission.commissionReceivedDate;
                }
              });

              // CS-2104: P&B download rows usually omit purchaseAndBill and have no V3
              // commission. Excel-only: if the invoice is Paid and V3 status is still
              // empty, write PAID (P&B flag when present, or no V3 commission on the payment).
              const v3PayStatusIndex = headerRow.indexOf(v3Export.PAY_STATUS);
              const v3ClearedDateIndex = headerRow.indexOf(v3Export.CLEARED_RECEIVED_DATE);
              const hasV3Commission = payment.commissions?.some((c: any) => isV3CommissionType(allCodeTablesValuesMap, c.agencyType));
              if (
                isInvoicePaid &&
                v3PayStatusIndex >= 0 &&
                !commissionRow[v3PayStatusIndex] &&
                (isPurchaseAndBill || !hasV3Commission)
              ) {
                commissionRow[v3PayStatusIndex] = "PAID";
              }
              if (
                v3ClearedDateIndex >= 0 &&
                !commissionRow[v3ClearedDateIndex] &&
                (isPurchaseAndBill || (!hasV3Commission && isInvoicePaid))
              ) {
                commissionRow[v3ClearedDateIndex] = payment.paymentDate;
              }

              commissionRows.push(commissionRow);
            });

            if (commissionRows.length > 0) {
              const startRow = worksheet.lastRow ? worksheet.lastRow.number + 1 : 2;

              commissionRows.forEach((cRow, idx) => {
                const finalRow = [...cRow];
                if (idx === 0) {
                  rowData.forEach((val: any, i: any) => {
                    if (!commissionSpecificCols.includes(headerRow[i])) {
                      // Format INV Amt column
                      if ((headerRow[i] === "INV Amt" || headerRow[i] === "NET INV Amt") && val) {
                        finalRow[i] = Number(val);
                      } else {
                        finalRow[i] = val;
                      }
                    }
                  });
                }
                //CS-1620 - Standardize Currency Formatting
                // worksheet.addRow(finalRow);
                const addedRow = worksheet.addRow(finalRow);
                const paymentAmountColIndex = headerRow.indexOf("Payment Amount") + 1;
                const netInvPmtAmtColIndex = headerRow.indexOf("NET INV PMT Amt") + 1;
                const v3CommColIndex = headerRow.indexOf(v3Export.SERVICE_FEE_AMOUNT) + 1;
                const agencyCommColIndex = headerRow.indexOf("Agency Svc Fee $") + 1;
                const overrideCommColIndex = headerRow.indexOf("Override $") + 1;
                const invAmtIndex = headerRow.indexOf("INV Amt") + 1;
                const netInvAmtIndex = headerRow.indexOf("NET INV Amt") + 1;
                const cogsPerUnitIndex = headerRow.indexOf("COGs $ P/U") + 1;
                const cogsTotalIndex = headerRow.indexOf("COGs $ Total") + 1;
                const gpAmountIndex = headerRow.indexOf("GP $") + 1;
                const gpPerUnitIndex = headerRow.indexOf("GP $ P/U") + 1;
                const gpPreCosIndex = headerRow.indexOf("GP $ Pre-COS") + 1;
                const gpPreCosPerUnitIndex = headerRow.indexOf("GP $ Pre-COS P/U") + 1;

                if (paymentAmountColIndex > 0) {
                  addedRow.getCell(paymentAmountColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (netInvPmtAmtColIndex > 0) {
                  addedRow.getCell(netInvPmtAmtColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (v3CommColIndex > 0) {
                  addedRow.getCell(v3CommColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (agencyCommColIndex > 0) {
                  addedRow.getCell(agencyCommColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (overrideCommColIndex > 0) {
                  addedRow.getCell(overrideCommColIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (invAmtIndex > 0) {
                  addedRow.getCell(invAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (netInvAmtIndex > 0) {
                  addedRow.getCell(netInvAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (cogsPerUnitIndex > 0) {
                  addedRow.getCell(cogsPerUnitIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (cogsTotalIndex > 0) {
                  addedRow.getCell(cogsTotalIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (gpAmountIndex > 0) {
                  addedRow.getCell(gpAmountIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (gpPerUnitIndex > 0) {
                  addedRow.getCell(gpPerUnitIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (gpPreCosIndex > 0) {
                  addedRow.getCell(gpPreCosIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
                if (gpPreCosPerUnitIndex > 0) {
                  addedRow.getCell(gpPreCosPerUnitIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
                }
              });

              const endRow = worksheet.lastRow!.number;

              headerRow.forEach((label: any, colIndex: any) => {
                if (!commissionSpecificCols.includes(label)) {
                  worksheet.mergeCells(startRow, colIndex + 1, endRow, colIndex + 1);
                }
              });

            } else {
              const addedRow = worksheet.addRow(rowData);
              const invAmtIndex = headerRow.indexOf("INV Amt") + 1;
              const netInvAmtIndex = headerRow.indexOf("NET INV Amt") + 1;
              if (invAmtIndex > 0) {
                addedRow.getCell(invAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
              }
              if (netInvAmtIndex > 0) {
                addedRow.getCell(netInvAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
              }
            }
          } else {
            const addedRow = worksheet.addRow(rowData);
            const invoiceTotalIndex = headerRow.indexOf("Invoice Total") + 1;
            const outstandingAmtIndex = headerRow.indexOf("Outstanding Amt") + 1;

            if (invoiceTotalIndex > 0) {
              addedRow.getCell(invoiceTotalIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
            }
            if (outstandingAmtIndex > 0) {
              addedRow.getCell(outstandingAmtIndex).numFmt = '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "0.00"??_);_(@_)';
            }
          }
        })
      }

      const columnWidth = 25;
      for (let i = 1; i <= headerRow.length; i++) {
        worksheet.getColumn(i).width = columnWidth;
      }

      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        if (rowNumber !== 1) {
          row.eachCell(cell => {
            cell.font = { size: 12 };
            cell.alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/octet-stream' });
      const link = document.createElement('a');

      link.href = URL.createObjectURL(blob);

      let fileName = "";
      if (sectionName === EXPORTABLE_ITEMS.INVOICE) {
        fileName = `Invoice_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.ORDER) {
        fileName = `Order_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.ORDER_SSV) {
        fileName = `Order_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.IVR) {
        fileName = `IVR_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.IVR_LIST) {
        fileName = `IVR_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1156 - Start
        //} else if (sectionName === EXPORTABLE_ITEMS.AGENCY) {
        //fileName = `Export_Agency_Detail_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.AGENCY_LIST) {
        fileName = `Agency_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.AGENCY_DETAILS) {
        fileName = `Agency_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1156 - End
        // CS-1370 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PRACTICE) {
        fileName = `Practice_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.PRACTICE_DETAILS) {
        fileName = `Practice_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1370 - End
        // CS-1405 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.AUDIT_LOG) {
        fileName = `Export_Audit_Log_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1405 - End
      } else if (sectionName === EXPORTABLE_ITEMS.COMMISSION) {
        fileName = `Service_Fee_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;

      } else if (sectionName === EXPORTABLE_ITEMS.AGREEMENT) {
        fileName = `Agreements_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.AGREEMENT_LIST) {
        fileName = `Agreements_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;

      } else if (sectionName === EXPORTABLE_ITEMS.PAYMENT) {
        fileName = `Payment_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1685 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.MANUFACTURER) {
        fileName = `Manufacturer_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.MANUFACTURER_DETAILS) {
        fileName = `Manufacturer_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.PROVIDER) {
        fileName = `Provider_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1685 - End
      } else if (sectionName === "USER") {
        fileName = `User_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.USER_DETAILS) {
        fileName = `User_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1685 - Start
      } else if (sectionName === EXPORTABLE_ITEMS.PRODUCTS) {
        fileName = `Products_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.PRODUCTS_DETAILS) {
        fileName = `Products_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.BAA) {
        fileName = `BAA_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1685 - End
        // CS-1679 Start
      } else if (sectionName === "activeUsers") {
        fileName = `Active_Users_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === "activePractices") {
        fileName = `Active_Practices_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1679 End
      } else if (sectionName === EXPORTABLE_ITEMS.PATIENTS) {
        fileName = `Patients_Search_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === EXPORTABLE_ITEMS.PATIENT_DETAILS) {
        fileName = `Patients_Details_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1679 Start
      } else if (sectionName === "totalPatients") {
        fileName = `Total_Patients_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === "weeklySales") {
        fileName = `Weekly_Sales_${new Date().toISOString().slice(0, 10)}.xlsx`;
      } else if (sectionName === "weeklyOrders") {
        fileName = `Weekly_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1679 End
        // CS-1706 START
      } else if (sectionName === EXPORTABLE_ITEMS.SERVICE_SUBSCRIPTION) {
        fileName = `Service_Subscription_List_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1706 END
        // CS-1728 START
      } else if (sectionName === EXPORTABLE_ITEMS.EXPLORE_MARKET) {
        fileName = `Explore_Market_Products_${new Date().toISOString().slice(0, 10)}.xlsx`;
        // CS-1728 END
      }


      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);


    } else {

    }
  } catch (error) {
    console.error('Error exporting to Excel:', error);
  }
};

export default ExportToExcel;
